import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { wallClock } from '@dataroom/core/clock';
import {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useUiT } from '../i18n';
import { resolveDismiss } from '../layers/base';
import { EditReportContext } from '../layers/edits';
import type { LayerCloseReason, LayerKind } from '../layers/types';
import { LayerParent, useLayer, useLayerStack, useParentLayerId } from '../layers/useLayer';
import { Button } from './Button';
import { cx } from './cx';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZES: Record<Exclude<DialogSize, 'full'>, number> = { sm: 440, md: 560, lg: 800, xl: 1080 };

export type DialogProps = {
  open: boolean;
  /** Called when the dialog closes itself (Escape, backdrop, ×, after "Discard"). */
  onClose: (reason?: LayerCloseReason) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Leading tile/icon in the header (e.g. an ObjectTile). */
  icon?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  /** 440 / 560 / 800 / 1080 px or full screen. Full screen below 640 px in any case. */
  size?: DialogSize;
  /** Explicit width in px (legacy; prefer `size`). */
  width?: number;
  /** Close on Escape and backdrop click (default true). The × button always closes. */
  dismissable?: boolean;
  className?: string;
  /** Extra class on the scrim / positioning container (`.scrim:has(...)` rules of features). */
  viewportClassName?: string;
  /** Hide the standard header (custom layouts like the command palette). */
  bare?: boolean;
  label?: string;
  /**
   * Element focused on open. Default: the first field, else the primary footer action — never the
   * close button. Pass a ref to focus something specific, or `false` to keep focus where it is.
   */
  initialFocus?: RefObject<HTMLElement | null> | false | number;
  /**
   * Unsaved input. `auto` (default for dialogs with a header) marks the dialog dirty as soon as the
   * user types into or changes a field; Escape, backdrop, × and route changes then ask once.
   */
  dirty?: boolean | 'auto';
  /** Wraps body and footer in a form: Enter submits (from inputs), ⌘↵ / Ctrl+↵ submits from anywhere. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** Layer kind (the command palette passes `palette`). */
  kind?: Extract<LayerKind, 'dialog' | 'palette' | 'alert' | 'presentation'>;
  /** Survives route changes. */
  pinned?: boolean;
  'data-testid'?: string;
};

const FIELD =
  'input:not([type="hidden"]):not(:disabled):not([readonly]), textarea:not(:disabled):not([readonly]), select:not(:disabled), [contenteditable="true"], [role="combobox"]:not([aria-disabled="true"]), [role="textbox"]';
const TABBABLE =
  'button:not(:disabled), [href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/** Content that is still loading: the first field is not there yet. */
const LOADING =
  '.modal-body [aria-busy="true"], .modal-body .spinner, .modal-body .skeleton, .modal-body [role="progressbar"]';

function visible(el: HTMLElement): boolean {
  return !el.closest('[hidden]') && el.getClientRects().length > 0;
}

/** The first field of the dialog's content (footer controls such as "Create another" do not count). */
export function firstDialogField(popup: HTMLElement): HTMLElement | null {
  const fields = Array.from(popup.querySelectorAll<HTMLElement>(FIELD)).filter(
    (el) =>
      !el.closest('[data-dialog-close], .modal-footer, .discard-bar') &&
      (visible(el) || el.getClientRects().length === 0),
  );
  return fields.find(visible) ?? fields[0] ?? null;
}

/**
 * Initial focus: the first field; while the content is still loading, the dialog itself (keys typed
 * meanwhile are kept and land in the first field once it appears); else the primary action; else the
 * first tabbable that is not "Close".
 */
export function initialDialogFocus(popup: HTMLElement | null): HTMLElement | true {
  if (!popup) return true;
  const field = firstDialogField(popup);
  if (field) return field;
  if (popup.querySelector(LOADING)) {
    if (!popup.hasAttribute('tabindex')) popup.setAttribute('tabindex', '-1');
    return popup;
  }
  const primary = popup.querySelector<HTMLElement>(
    '.modal-footer .btn-p:not(:disabled), [data-primary]:not(:disabled)',
  );
  if (primary) return primary;
  const other = Array.from(popup.querySelectorAll<HTMLElement>(TABBABLE)).find(
    (el) => !el.closest('[data-dialog-close]'),
  );
  return other ?? popup;
}

const TEXT_INPUT_TYPES = new Set(['', 'text', 'search', 'email', 'url', 'tel', 'password', 'number']);

function isTextField(el: EventTarget | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  return (
    el instanceof HTMLInputElement &&
    TEXT_INPUT_TYPES.has(el.getAttribute('type') ?? '') &&
    !el.readOnly &&
    !el.disabled
  );
}

function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    el.isContentEditable
  );
}

/** Append text to a (React-controlled) input as if typed: native setter plus an `input` event. */
function typeInto(el: HTMLInputElement | HTMLTextAreaElement, text: string) {
  const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el) as object, 'value')?.set;
  if (!setter) return;
  setter.call(el, el.value + text);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/** How long a dialog waits for its first field (content loading) before giving up the type-ahead. */
const LATE_FIELD_MS = 4000;

/**
 * Late first field: when a dialog opens before its content loaded (a form waiting for its layout),
 * focus waits on the dialog, keys typed meanwhile are kept, and both move to the first field as soon
 * as it appears — unless the member moved focus or clicked in the meantime.
 */
export function useLateFieldFocus(popupRef: RefObject<HTMLElement | null>, open: boolean) {
  const state = useRef({ pending: false, buffer: '', until: 0, initial: null as Element | null });
  // Keys typed while the content loads are unsaved input: the dialog counts as dirty meanwhile.
  const [holding, setHolding] = useState(false);
  const resolve = useCallback((popup: HTMLElement | null): HTMLElement | true => {
    const target = initialDialogFocus(popup);
    const isField = target !== true && popup !== null && target === firstDialogField(popup);
    state.current = {
      pending: !isField,
      buffer: '',
      until: wallClock.nowMs() + LATE_FIELD_MS,
      initial: target === true ? null : target,
    };
    return target;
  }, []);
  const active = () => state.current.pending && wallClock.nowMs() < state.current.until;

  useEffect(() => {
    if (!open) {
      state.current.pending = false;
      state.current.buffer = '';
      setHolding(false);
      return;
    }
    let observer: MutationObserver | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attach = () => {
      const popup = popupRef.current;
      if (!popup) {
        timer = setTimeout(attach, 16);
        return;
      }
      observer = new MutationObserver(() => {
        if (!active()) {
          observer?.disconnect();
          return;
        }
        const field = firstDialogField(popup);
        if (!field || field.closest('[hidden]')) return;
        // The first field is there: the keys typed meanwhile land in it, and focus follows while it is
        // still where the dialog put it (a field that focused itself, autoFocus, keeps focus).
        const text = state.current.buffer;
        state.current.pending = false;
        state.current.buffer = '';
        observer?.disconnect();
        const focused = document.activeElement;
        if (focused === popup || focused === state.current.initial || focused === document.body)
          field.focus();
        if (text && isTextField(field)) typeInto(field, text);
        setHolding(false);
      });
      observer.observe(popup, { childList: true, subtree: true });
    };
    attach();
    const stop = setTimeout(() => {
      state.current.pending = false;
      state.current.buffer = '';
      setHolding(false);
      observer?.disconnect();
    }, LATE_FIELD_MS);
    return () => {
      clearTimeout(timer);
      clearTimeout(stop);
      observer?.disconnect();
    };
  }, [open, popupRef]);

  const handlers = {
    onKeyDownCapture: (e: KeyboardEvent<HTMLElement>) => {
      if (!active() || e.currentTarget !== document.activeElement || isEditable(e.target)) return;
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        state.current.buffer += e.key;
        e.preventDefault();
        setHolding(true);
      } else if (e.key === 'Backspace' && state.current.buffer) {
        state.current.buffer = state.current.buffer.slice(0, -1);
        e.preventDefault();
        setHolding(state.current.buffer.length > 0);
      }
    },
    onPointerDown: (e: { target: EventTarget }) => {
      // Clicking into the content takes over; answering "Keep editing" does not.
      if (e.target instanceof Element && e.target.closest('.discard-bar')) return;
      state.current.pending = false;
    },
  };
  return { resolve, handlers, holding };
}

/** Inline "Discard changes? / Keep editing" bar of a dirty dialog or drawer. */
export function DiscardBar({ onKeep, onDiscard }: { onKeep: () => void; onDiscard: () => void }) {
  const t = useUiT();
  const keepRef = useRef<HTMLButtonElement>(null);
  // Focus the safe choice; "Keep editing" puts focus back where the member was typing.
  const [previous] = useState<Element | null>(() =>
    typeof document === 'undefined' ? null : document.activeElement,
  );
  useEffect(() => keepRef.current?.focus(), []);
  const barRef = useRef<HTMLDivElement>(null);
  const keep = () => {
    // Move focus before the bar unmounts, so focus is never lost (and never re-homed by the dialog):
    // back to where the member was typing, else (a dialog that was still loading held focus itself,
    // or that element is gone) to the first field of the dialog.
    const bar = barRef.current;
    const host = bar?.parentElement?.closest<HTMLElement>('[role="dialog"]') ?? null;
    const usable =
      previous instanceof HTMLElement &&
      previous.isConnected &&
      previous !== host &&
      previous !== document.body &&
      !bar?.contains(previous) &&
      !previous.closest('[data-dialog-close]') &&
      (!host || host.contains(previous));
    const target: HTMLElement | null = usable
      ? (previous as HTMLElement)
      : host
        ? (firstDialogField(host) ?? host)
        : null;
    target?.focus();
    onKeep();
  };
  return (
    <div
      ref={barRef}
      className="discard-bar"
      role="alertdialog"
      aria-modal="false"
      aria-label={t('ui.discard.title')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          keep();
        }
      }}
    >
      <div className="discard-text">
        <strong>{t('ui.discard.title')}</strong>
        <span>{t('ui.discard.body')}</span>
      </div>
      <div className="discard-actions">
        <Button ref={keepRef} size="sm" onClick={keep}>
          {t('ui.discard.keep')}
        </Button>
        <Button size="sm" variant="danger" onClick={onDiscard} data-discard="">
          {t('ui.discard.confirm')}
        </Button>
      </div>
    </div>
  );
}

/** Pending discard confirmation of a layer: renders `DiscardBar` and resolves the stack's promise. */
export function useDiscardConfirm(open = true) {
  const [pending, setPending] = useState<((discard: boolean) => void) | null>(null);
  // A layer that closes while the question is open (Cancel, a route change) takes the question with it: a
  // dialog that stays mounted and opens again must not show the old "Discard changes?" bar.
  useEffect(() => {
    if (!open && pending) pending(false);
  }, [open, pending]);
  const ask = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        setPending(() => (discard: boolean) => {
          setPending(null);
          resolve(discard);
        });
      }),
    [],
  );
  const bar = pending ? <DiscardBar onKeep={() => pending(false)} onDiscard={() => pending(true)} /> : null;
  return { ask, bar, asking: pending !== null };
}

/** Tracks user edits inside a container (`dirty="auto"`): typing, choosing, toggling. */
export function useAutoDirty(open: boolean, mode: boolean | 'auto' | undefined) {
  const [edited, setEdited] = useState(false);
  useEffect(() => {
    if (!open) setEdited(false);
  }, [open]);
  const onEdit = useCallback(
    (e: { target: EventTarget }) => {
      if (mode !== 'auto' || edited) return;
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest('[data-dirty-ignore], .discard-bar')) return;
      if (target instanceof HTMLInputElement && (target.type === 'search' || target.type === 'hidden'))
        return;
      const role = target.getAttribute('role');
      if (role === 'searchbox' || target.getAttribute('aria-autocomplete') === 'list') return;
      setEdited(true);
    },
    [mode, edited],
  );
  // A picker inside (its list portaled into its own layer) reports its edits through context.
  const report = useCallback(() => {
    if (mode === 'auto') setEdited(true);
  }, [mode]);
  return { dirty: mode === 'auto' ? edited : Boolean(mode), onEdit, report };
}

/** Submit the dialog's form (⌘↵), else press its primary action. */
function submitShortcut(e: KeyboardEvent<HTMLElement>) {
  if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey) || e.defaultPrevented) return;
  const popup = e.currentTarget;
  const form = (e.target as Element).closest('form') ?? popup.querySelector('form');
  e.preventDefault();
  if (form instanceof HTMLFormElement && popup.contains(form)) {
    form.requestSubmit();
    return;
  }
  popup
    .querySelector<HTMLElement>('.modal-footer .btn-p:not(:disabled), [data-primary]:not(:disabled)')
    ?.click();
}

/**
 * Centered dialog on the layer stack (Base UI Dialog): focus trap, `inert` background, scroll lock,
 * initial focus on the first field, sticky footer, ⌘↵ submits, Escape / backdrop / × ask once when
 * dirty. Below 640 px it is full screen with the footer pinned to the bottom.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  icon,
  footer,
  children,
  size = 'md',
  width,
  dismissable = true,
  className,
  viewportClassName,
  bare,
  label,
  initialFocus,
  dirty: dirtyProp,
  onSubmit,
  kind = 'dialog',
  pinned,
  'data-testid': testId,
}: DialogProps) {
  const t = useUiT();
  const titleId = useId();
  const popupRef = useRef<HTMLDivElement>(null);
  const { ask, bar } = useDiscardConfirm(open);
  const { dirty, onEdit, report } = useAutoDirty(open, dirtyProp ?? (bare ? false : 'auto'));
  const lateField = useLateFieldFocus(popupRef, open && initialFocus === undefined);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const layer = useLayer({
    kind,
    open,
    onClose: (reason) => onCloseRef.current(reason),
    escape: 'self',
    dirty: dirty || lateField.holding,
    pinned,
    confirmDiscard: ask,
    label: typeof title === 'string' ? title : label,
  });

  const dialogWidth = width ?? (size === 'full' ? undefined : SIZES[size]);
  const style = (dialogWidth ? { '--dialog-w': `${dialogWidth}px` } : {}) as CSSProperties;
  const body = bare ? children : <div className="modal-body">{children}</div>;
  const foot = footer ? <div className="modal-footer">{footer}</div> : null;

  return (
    <BaseDialog.Root
      open={open}
      modal
      disablePointerDismissal={!dismissable}
      onOpenChange={(next, details) => {
        if (next) return;
        if (!dismissable && (details.reason === 'escape-key' || details.reason === 'outside-press')) {
          details.cancel();
          return;
        }
        const reason = resolveDismiss(layer, details);
        if (reason) onClose(reason);
      }}
    >
      <BaseDialog.Portal container={layer.container} className="dr-layer" {...layer.portalProps}>
        <BaseDialog.Viewport
          className={cx('scrim', 'dialog-viewport', size === 'full' && 'full', viewportClassName)}
        >
          <BaseDialog.Popup
            ref={popupRef}
            className={cx('modal', `modal-${size}`, className)}
            style={style}
            data-testid={testId}
            initialFocus={
              initialFocus === false
                ? false
                : initialFocus && typeof initialFocus === 'object'
                  ? initialFocus
                  : typeof initialFocus === 'number'
                    ? () => initialDialogFocus(popupRef.current)
                    : () => lateField.resolve(popupRef.current)
            }
            {...(initialFocus === undefined ? lateField.handlers : {})}
            {...(bare
              ? { 'aria-label': label ?? (typeof title === 'string' ? title : undefined) }
              : { 'aria-labelledby': titleId })}
            finalFocus={layer.finalFocus}
            onKeyDown={submitShortcut}
            onInput={onEdit}
            onChange={onEdit}
          >
            <LayerParent id={layer.id}>
              <EditReportContext.Provider value={report}>
                {bar}
                {bare ? null : (
                  <div className="modal-header">
                    {icon}
                    <div className="modal-heading">
                      <BaseDialog.Title id={titleId} className="modal-title">
                        {title}
                      </BaseDialog.Title>
                      {description ? (
                        <BaseDialog.Description className="modal-desc">{description}</BaseDialog.Description>
                      ) : null}
                    </div>
                    <Button
                      variant="ghost"
                      iconOnly
                      icon="x"
                      label={t('ui.close')}
                      data-dialog-close=""
                      tooltip={false}
                      onClick={() => void layer.requestClose('close-button')}
                    />
                  </div>
                )}
                {onSubmit ? (
                  <form
                    className="modal-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSubmit(e);
                    }}
                  >
                    {body}
                    {foot}
                  </form>
                ) : (
                  <>
                    {body}
                    {foot}
                  </>
                )}
              </EditReportContext.Provider>
            </LayerParent>
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

/**
 * Inside a dialog's (or drawer's) content: close it the way its × does, so a dirty form asks
 * "Discard changes?" once. For footer actions such as Cancel. Resolves true when it closed.
 */
export function useDialogClose(): () => Promise<boolean> {
  const stack = useLayerStack();
  const id = useParentLayerId();
  return useCallback(
    () => (id ? stack.requestClose(id, 'close-button') : Promise.resolve(false)),
    [stack, id],
  );
}

/** Legacy name of `Dialog` (65 call sites); same props. */
export const Modal = Dialog;
export type ModalProps = DialogProps;
